import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Divider,
  Flex,
  FormControl,
  FormLabel,
  Input,
  SimpleGrid,
  Stack,
  Text,
  Textarea,
  useColorModeValue,
  useToast,
} from "@chakra-ui/react";
import Card from "components/card/Card";
import { getPrivateBooking, updatePrivateBooking } from "Api/apiService";

const emptyHighlight = {
  title: "",
  description: "",
  icon: "",
};

export default function PrivateBookingAdmin() {
  const [heroTitle, setHeroTitle] = useState("");
  const [heroSubtitle, setHeroSubtitle] = useState("");
  const [highlights, setHighlights] = useState([emptyHighlight]);
  const [formTitle, setFormTitle] = useState("");
  const [formSubtitle, setFormSubtitle] = useState("");
  const [eventTypesText, setEventTypesText] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const toast = useToast();

  const textColor = useColorModeValue("navy.700", "white");
  const labelColor = useColorModeValue("secondaryGray.600", "whiteAlpha.700");

  const loadContent = async () => {
    setLoading(true);
    try {
      const response = await getPrivateBooking();
      const payload = response?.data?.privateBooking;
      setHeroTitle(payload?.heroTitle || "");
      setHeroSubtitle(payload?.heroSubtitle || "");
      if (Array.isArray(payload?.highlights) && payload.highlights.length > 0) {
        setHighlights(payload.highlights);
      } else {
        setHighlights([emptyHighlight]);
      }
      setFormTitle(payload?.formTitle || "");
      setFormSubtitle(payload?.formSubtitle || "");
      setEventTypesText(
        Array.isArray(payload?.eventTypes) ? payload.eventTypes.join("\n") : ""
      );
    } catch (error) {
      console.error("Failed to load private booking content", error);
      toast({
        title: "Unable to load Private Booking content.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadContent();
  }, []);

  const handleHighlightChange = (index, key) => (event) => {
    const value = event.target.value;
    setHighlights((prev) =>
      prev.map((highlight, idx) =>
        idx === index ? { ...highlight, [key]: value } : highlight
      )
    );
  };

  const addHighlight = () => {
    setHighlights((prev) => [...prev, emptyHighlight]);
  };

  const removeHighlight = (index) => {
    setHighlights((prev) =>
      prev.length === 1 ? prev : prev.filter((_, idx) => idx !== index)
    );
  };

  const handleSave = async () => {
    if (!heroTitle.trim() || !heroSubtitle.trim()) {
      toast({
        title: "Hero title and subtitle are required.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    if (!formTitle.trim() || !formSubtitle.trim()) {
      toast({
        title: "Form title and subtitle are required.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    setSaving(true);
    try {
      const payload = {
        heroTitle: heroTitle.trim(),
        heroSubtitle: heroSubtitle.trim(),
        highlights: highlights
          .filter((highlight) => highlight.title.trim() && highlight.description.trim())
          .map((highlight) => ({
            title: highlight.title.trim(),
            description: highlight.description.trim(),
            icon: highlight.icon.trim(),
          })),
        formTitle: formTitle.trim(),
        formSubtitle: formSubtitle.trim(),
        eventTypes: eventTypesText
          .split("\n")
          .map((line) => line.trim())
          .filter(Boolean),
      };
      await updatePrivateBooking(payload);
      toast({
        title: "Private Booking updated.",
        status: "success",
        duration: 2500,
        isClosable: true,
      });
      loadContent();
    } catch (error) {
      console.error("Failed to update Private Booking", error);
      toast({
        title: "Unable to update Private Booking.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box pt={{ base: "130px", md: "80px", xl: "80px" }}>
      <SimpleGrid columns={{ base: 1 }} spacing="20px">
        <Card p="24px">
          <Flex direction="column" gap="16px">
            <Text fontSize="22px" fontWeight="700" color={textColor}>
              Private Booking Information
            </Text>
            <Text fontSize="14px" color={labelColor}>
              Update hero, highlights, and event type list shown on mobile.
            </Text>
            <Divider />
            <Stack spacing="14px">
              <FormControl>
                <FormLabel color={labelColor}>Hero Title</FormLabel>
                <Input value={heroTitle} onChange={(e) => setHeroTitle(e.target.value)} />
              </FormControl>
              <FormControl>
                <FormLabel color={labelColor}>Hero Subtitle</FormLabel>
                <Input
                  value={heroSubtitle}
                  onChange={(e) => setHeroSubtitle(e.target.value)}
                />
              </FormControl>
            </Stack>
          </Flex>
        </Card>

        <Card p="24px">
          <Flex direction="column" gap="16px">
            <Flex justify="space-between" align="center">
              <Text fontSize="22px" fontWeight="700" color={textColor}>
                Highlights
              </Text>
              <Button variant="outline" onClick={addHighlight}>
                Add Highlight
              </Button>
            </Flex>
            <Divider />
            <Stack spacing="14px">
              {highlights.map((highlight, index) => (
                <Box
                  key={`highlight-${index}`}
                  borderWidth="1px"
                  borderColor="gray.200"
                  borderRadius="12px"
                  p="16px"
                >
                  <Flex justify="space-between" align="center" mb="12px">
                    <Text fontWeight="700" color={textColor}>
                      Highlight {index + 1}
                    </Text>
                    <Button
                      size="sm"
                      variant="ghost"
                      colorScheme="red"
                      onClick={() => removeHighlight(index)}
                      isDisabled={highlights.length === 1}
                    >
                      Remove
                    </Button>
                  </Flex>
                  <Stack spacing="12px">
                    <FormControl>
                      <FormLabel color={labelColor}>Icon (emoji)</FormLabel>
                      <Input
                        value={highlight.icon}
                        onChange={handleHighlightChange(index, "icon")}
                      />
                    </FormControl>
                    <FormControl>
                      <FormLabel color={labelColor}>Title</FormLabel>
                      <Input
                        value={highlight.title}
                        onChange={handleHighlightChange(index, "title")}
                      />
                    </FormControl>
                    <FormControl>
                      <FormLabel color={labelColor}>Description</FormLabel>
                      <Textarea
                        value={highlight.description}
                        onChange={handleHighlightChange(index, "description")}
                        rows={3}
                      />
                    </FormControl>
                  </Stack>
                </Box>
              ))}
            </Stack>
          </Flex>
        </Card>

        <Card p="24px">
          <Flex direction="column" gap="16px">
            <Text fontSize="22px" fontWeight="700" color={textColor}>
              Event Types & Form Header
            </Text>
            <Text fontSize="14px" color={labelColor}>
              This updates the form heading and the event type chips shown on mobile.
            </Text>
            <Divider />
            <Stack spacing="14px">
              <FormControl>
                <FormLabel color={labelColor}>Form Title</FormLabel>
                <Input value={formTitle} onChange={(e) => setFormTitle(e.target.value)} />
              </FormControl>
              <FormControl>
                <FormLabel color={labelColor}>Form Subtitle</FormLabel>
                <Input
                  value={formSubtitle}
                  onChange={(e) => setFormSubtitle(e.target.value)}
                />
              </FormControl>
              <FormControl>
                <FormLabel color={labelColor}>Event Types (one per line)</FormLabel>
                <Textarea
                  value={eventTypesText}
                  onChange={(e) => setEventTypesText(e.target.value)}
                  rows={4}
                />
              </FormControl>
            </Stack>
          </Flex>
        </Card>

        <Flex justify="flex-end">
          <Button
            colorScheme="blue"
            onClick={handleSave}
            isLoading={saving}
            isDisabled={loading}
          >
            Save Changes
          </Button>
        </Flex>
      </SimpleGrid>
    </Box>
  );
}
